import { APP_INITIALIZER, LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';

import { ViewportScroller } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, Scroll } from '@angular/router';
import { CoreModule } from '@core/core.module';
import { AnalyticsDirective } from '@core/services/analytics.directive';
import { ApiService } from '@core/services/api.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { LanguageService } from '@shared/services/language.service';
import { TranslationLoaderService } from '@shared/services/translation.service';
import { BlockUIModule } from 'ng-block-ui';
import { BlockUIHttpModule } from 'ng-block-ui/http';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
} from '@angular/common/http';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FooterComponent } from '@core/components/footer/footer.component';
import { HeaderMobileComponent } from '@core/components/header-mobile/header-mobile.component';
import { HeaderComponent } from '@core/components/header/header.component';
import Aura from '@primeng/themes/aura';
import { NoPaymentMethodDialogComponent } from '@shared/components/no-payment-method-dialog/no-payment-method-dialog.component';
import { SuggestedAuctionsComponent } from '@shared/components/suggested-auctions/suggested-auctions.component';
import { CookieInterceptor } from '@shared/interceptors/cookie.interceptor';
import { NgxMaskDirective, provideEnvironmentNgxMask } from 'ngx-mask';
import { providePrimeNG } from 'primeng/config';

export function getLanguage(languageService: LanguageService) {
  if (!languageService.getWindow()) {
    return 'it';
  }
  return languageService.getWindow().navigator.language == 'it'
    ? languageService.getWindow().navigator.language
    : 'en';
}

@NgModule({
  declarations: [AppComponent, AnalyticsDirective],
  imports: [
    BrowserModule.withServerTransition({ appId: 'memorabid' }),
    AppRoutingModule,
    FontAwesomeModule,
    CoreModule,
    SuggestedAuctionsComponent,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [ApiService],
      },
    }),
    NgxMaskDirective,
    BlockUIModule.forRoot({
      delayStart: 150,
      delayStop: 10,
    }),
    BlockUIHttpModule.forRoot({
      requestFilters: [
        /geteventnotification|gethistory|geteventdateend|getauctionpriceinfo|getcountunreadmessages|get-current-prices|get-suggested|gettotalamount|memorabidevent\/getbypk\/|memorabidevent\/getbyslug/,
      ],
    }),
    FormsModule,
    RecaptchaV3Module,
    HeaderComponent,
    HeaderMobileComponent,
    FooterComponent,
    NoPaymentMethodDialogComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
      scope: '/',
    }),
    ServiceWorkerModule.register('custom-sw.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
      scope: '/',
    }),
  ],
  exports: [AnalyticsDirective],
  providers: [
    {
      provide: LOCALE_ID,
      //useValue: "it-IT"
      deps: [LanguageService],
      useFactory: getLanguage,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CookieInterceptor, multi: true },
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includeRequestsWithAuthHeaders: true,
        filter: (a) => {
          if (a.method === 'GET') {
            return (
              a.url.includes('pagetext') ||
              a.url.toLocaleLowerCase().includes('publicgeneric') ||
              a.url.includes('entitydata/getbasedatabypk') ||
              a.url.includes('tag/getbyslug') ||
              a.url.includes('api/filter') ||
              a.url.includes('api/Translation/getalllanguages') ||
              a.url.includes('auction/getdefaultdata') ||
              a.url.includes('auctionView/get-suggested') ||
              a.url.includes('api/categories/related/by-tag-and-seller')
            );
          }
          return false;
        },
      })
    ),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideEnvironmentNgxMask(),
    provideHttpClient(withFetch()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          dark: false,
          darkModeSelector: false,
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private _router: Router, _viewportScroller: ViewportScroller) {
    this._router.events
      .pipe(filter((event) => event instanceof Scroll))
      .subscribe((event) => {
        const e = event as Scroll;
        if (e.anchor) {
          // anchor navigation
          /* setTimeout is the core line to solve the solution */
          setTimeout(() => {
            _viewportScroller.scrollToAnchor(e.anchor!);
          });
        } else if (e.position) {
          // backward navigation
          _viewportScroller.scrollToPosition(e.position);
        } else {
          // forward navigation
          _viewportScroller.scrollToPosition([0, 0]);
        }
      });
  }
}
